<template>
    <form @submit.prevent="">
        <creditcard-networks :brand="cardBrand" class="mb-3" />
        <div class="add-credit-card-form credit-card-inputs">
            <div class="row mb-3">
                <div class="col">
                    <div class="card-number">
                        <div class="form-group">
                            <label :class="{ 'text-danger' : errors.first('cardNumber') }">
                                <span>Card Number</span>
                                <img v-tooltip="'Transactions are encrypted and secure.'" src="@assets/img/padlock-icon.svg" alt="Padlock">
                            </label>
                            <card-number
                                ref="cardNumber"
                                :class="{ 'is-danger' : errors.first('cardNumber') }"
                                class="card-number form-control"
                                :stripe="stripeKey"
                                :options="stripeOptions"
                                @change="handleChange('cardNumber', $event)"
                            />
                            <span v-show="errors.has('cardNumber')" class="text-danger mt-1">
                                {{ errors.first('cardNumber') }}
                            </span>
                        </div>
                    </div>
                </div>
            </div>
            <div class="row mb-3">
                <div class="col">
                    <div class="form-group">
                        <label :class="{ 'text-danger' : errors.first('cardExpiry') }" for="expiration">
                            <span>Expiration</span>
                        </label>
                        <card-expiry
                            ref="cardExpiry"
                            :class="{ 'is-danger' : errors.first('cardExpiry') }"
                            class="card-expiry form-control"
                            :stripe="stripeKey"
                            :options="stripeOptions"
                            @change="handleChange('cardExpiry', $event)"
                        />
                        <span v-show="errors.has('cardExpiry')" class="text-danger mt-1">
                            {{ errors.first('cardExpiry') }}
                        </span>
                    </div>
                </div>
                <div class="col">
                    <div class="form-group">
                        <label :class="{ 'text-danger' : errors.first('cardCvc') }" for="cvc">
                            <span class="mr-1">
                                CVC
                            </span>
                            <img
                                v-tooltip="'The security code, or CVV, refers to the extra 3 or 4 numbers on the back or front of your card.'"
                                class="mr-auto"
                                src="@assets/img/help-icon.png"
                                alt="Help"
                            >
                        </label>
                        <card-cvc
                            ref="cardCvc"
                            class="card-cvc form-control"
                            :class="{ 'is-danger' : errors.first('cardCvc') }"
                            :stripe="stripeKey"
                            :options="stripeOptions"
                            @change="handleChange('cardCvc', $event)"
                        />
                        <span v-show="errors.has('cardCvc')" class="text-danger mt-1">
                            {{ errors.first('cardCvc') }}
                        </span>
                    </div>
                </div>
                <div v-if="zipCode" class="col">
                    <div class="form-group">
                        <label :class="{ 'text-danger' : errors.first('postalCode') }" for="zip-code">
                            <span>Zip Code</span>
                        </label>
                        <postal-code
                            ref="postalCode"
                            class="card-postal-code form-control"
                            :class="{ 'is-danger' : errors.first('postalCode') }"
                            :stripe="stripeKey"
                            :options="stripeOptions"
                            @change="handleChange('postalCode', $event)"
                        />
                        <span v-show="errors.has('postalCode')" class="text-danger mt-1">
                            {{ errors.first('postalCode') }}
                        </span>
                    </div>
                </div>
            </div>
        </div>
        <span v-show="errors.has('stripe_errors')" class="text-danger">
            {{ errors.first('stripe_errors') }}
        </span>
    </form>
</template>

<script>

import {
    CardNumber,
    CardExpiry,
    CardCvc,
    PostalCode,
    createSource
} from "vue-stripe-elements-plus";

export default {
    name: "Card",
    components: {
        CardNumber,
        CardExpiry,
        CardCvc,
        PostalCode
    },
    props: {
        zipCode: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            stripeKey: process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY,
            complete: false,
            isComplete: {
                cardExpiry: false,
                cardCvc: false,
                cardNumber: false
                // postalCode: false
            },
            stripeOptions: {
                style: {
                    base: {
                        fontFamily: "Gibson, sans-serif",
                        color: "white"
                    },
                    invalid: {
                        color: "#d0021b"
                    }
                },
                fontFamily: "Gibson, sans-serif",
                base: "form-control",
                invalid: "form-control is-danger"
            },
            cardBrand: "unknown"
        }
    },
    watch: {
        isComplete: {
            handler() {
                this.reportValidFields();
            },
            deep: true
        }
    },
    created() {
        this.$root.$on("reset-stripe-elements", this.resetElements);
    },
    methods: {
        reportValidFields() {
            if (this.validateFields()) {
                this.errors.remove("stripe_errors");
                createSource().then(cardInfo => {
                    if (cardInfo.error) {
                        this.errors.add({
                            field: "stripe_errors",
                            msg: cardInfo.error.message
                        });
                        return;
                    }
                    this.$emit("striped-card", cardInfo.source.id)
                })
            } else {
                this.$emit("striped-card", "");
            }
        },
        validateFields() {
            return Object.values(this.isComplete).every(value => value);
        },
        handleChange(field, event) {
            if (event.error) {
                if (this.errors.has(event.elementType)) {
                    this.errors.clear(event.elementType);
                }
                this.errors.add({
                    id: event.error.code,
                    field: event.elementType,
                    msg: event.error.message
                });
            } else {
                this.errors.remove(event.elementType)
            }

            if (event.elementType == "cardNumber" && event.brand) {
                this.cardBrand = event.brand;
            }

            this.isComplete[field] = event.complete;
        },
        clear() {
            Object.keys(this.isComplete).forEach((key) => {
                this.$refs[key].clear();
            })
        },
        resetElements() {
            ["cardNumber", "cardExpiry", "cardCvc"].forEach(element => {
                if (this.$refs[element]) {
                    this.$refs[element].$destroy();
                }
            });
        }
    }
}
</script>


<style lang="scss">
    .add-credit-card-form {
        .row {
            margin-left: -5px;
            margin-right: -5px;

            .col {
                padding-left: 5px;
                padding-right: 5px;
            }
        }


        .form-group {
            .form-control {
                display: flex;
                padding: 0;

                .StripeElement  {
                    width: 100%;
                    padding: 0.85rem .75rem;
                }
            }
        }
    }
</style>
